// hooks/useMediaQuery.js
// Subscribe a component to a CSS media query. Returns true while the query
// matches and re-renders on change (orientation, window resize, zoom).
//
// Server render and browsers without matchMedia report false, so callers
// must pick the default that is safe to render without the query.
import { useEffect, useState } from "react";

const safeMatch = (query) => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia(query).matches;
  } catch (err) {
    return false;
  }
};

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => safeMatch(query));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    let mql;
    try {
      mql = window.matchMedia(query);
    } catch (err) {
      return undefined;
    }
    const sync = () => setMatches(mql.matches);
    sync();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", sync);
      return () => mql.removeEventListener("change", sync);
    }
    // Safari before 14 only has the deprecated pair.
    mql.addListener(sync);
    return () => mql.removeListener(sync);
  }, [query]);

  return matches;
};

export default useMediaQuery;
